import { useState, useEffect, useCallback } from 'react';
import { EllipsisVertical } from 'lucide-react';
import { useOrganization } from '@clerk/clerk-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { CustomError, Invitation } from './merchant-type';
import { MultiSelector } from '../components/multi-selector';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { MEMBER_ROLE } from '@/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { OrganizationMembershipResource } from '@clerk/types';
import { isContextualEmail } from '@contextual/common';
const formSchema = z.object({
  role: z.string().min(1, 'Role is required.'),
  memberRole: z.string().min(1, 'Role is required.'),
  emailValue: z
    .string()
    .min(1, 'Email is required.')
    .refine(
      (value) => {
        const emails = value.split(/[\s,]+/).filter((email) => email.length > 0); // Split by spaces or commas
        return emails.every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)); // Validate each email
      },
      {
        message: 'Enter valid email addresses, separated by spaces or commas.'
      }
    )
});

export function TeamInformationForm() {
  const { organization, membership } = useOrganization();

  const [membersListData, setMembersListData] = useState<OrganizationMembershipResource[] | []>([]);
  const [invitationListData, setInvitationListData] = useState<Invitation[] | []>([]);

  const isAdmin = membership?.role === 'org:admin';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      role: 'member',
      memberRole: 'member',
      emailValue: ''
    },
    mode: 'onChange'
  });

  const handleMembersRole = async (userId: string, role: string) => {
    try {
      await organization?.updateMember({ userId, role: `org:${role}` });
      toast({
        title: 'Role updated successfully!',
        variant: 'default'
      });
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error -- handleMembersRole',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  };

  const handleRevokeInvitation = async (user: Invitation) => {
    try {
      await user.revoke?.();
      toast({
        title: 'Invitation revoked successfully!',
        variant: 'default'
      });
      await fetchInvitations();
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error -- handleRevokeInvitation',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendInvitation = async () => {
    try {
      const emailAddresses = form
        .getValues('emailValue')
        .split(/[,\s]+/) // Split by commas or spaces
        .map((email) => email.trim()) // Remove extra spaces
        .filter((email) => validateEmail(email)); // Validate emails
      const role = `org:${form.getValues('role')}`;
      await organization?.inviteMembers({ emailAddresses, role });
      await fetchInvitations();
      form.setValue('emailValue', '');
      toast({
        title: 'Invitations sent successfully!',
        variant: 'default'
      });
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await organization?.removeMember(userId);
      toast({
        title: 'Member removed successfully!',
        variant: 'default'
      });
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error -- handleRemoveMember',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  };

  const fetchInvitations = useCallback(async () => {
    try {
      const invitationsResponse = await organization?.getInvitations();
      const filteredData =
        invitationsResponse?.data.filter((item) => item.status === 'pending') ?? [];
      setInvitationListData(filteredData as Invitation[] | []);
    } catch (error) {
      const typedError = error as CustomError;
      toast({
        title: 'Error -- fetchInvitations',
        description: typedError.errors[0].longMessage ?? 'Something went wrong',
        variant: 'destructive'
      });
    }
  }, [organization]);

  useEffect(() => {
    if (isAdmin) {
      void fetchInvitations();
    }
  }, []);

  useEffect(() => {
    const getMembers = async () => {
      try {
        const membersResponse = await organization?.getMemberships();
        const membersData = membersResponse?.data ?? [];
        const filteredMembers = membersData.filter(
          (member) => !isContextualEmail(member.publicUserData.identifier)
        );
        setMembersListData(filteredMembers);
      } catch (error) {
        const typedError = error as CustomError;
        toast({
          title: 'Error -- getMembers',
          description: typedError.errors[0].longMessage ?? 'Something went wrong',
          variant: 'destructive'
        });
      }
    };
    void getMembers();
  }, [organization]);

  return (
    <TooltipProvider>
      <Form {...form}>
        <form
          onSubmit={(e) => {
            void form.handleSubmit(handleSendInvitation)(e);
          }}
          className="space-y-4"
        >
          <div className="rounded-lg">
            <div className="grid mt-8 sm:grid-cols-1 gap-4 justify-center">
              <div className="col-span-1 sm:col-span-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-xl font-semibold text-foreground">Invite</div>
                  </TooltipTrigger>
                  {!isAdmin && (
                    <TooltipContent>Only administrators can invite new members</TooltipContent>
                  )}
                </Tooltip>
              </div>
              <div className="col-span-1 sm:col-span-1">
                <div className="text-base font-semibold text-foreground">
                  Invite new team members
                </div>
                <div className="text-sm font-normal text-muted-foreground">
                  Enter or paste one or more email addresses, separated by spaces or commas.
                </div>
                <div className=" mt-4">
                  <FormField
                    control={form.control}
                    name="emailValue"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormControl>
                          <div className="bg-card border border-gray rounded-lg mt-4">
                            <textarea
                              className={`w-full bg-card p-3 h-[80px] rounded-lg focus:outline-none resize-none placeholder:text-muted-foreground placeholder:text-font-normal placeholder:text-sm
                                ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                              placeholder="example@email.com, example2@email.com"
                              rows={3}
                              disabled={!isAdmin}
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-700 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="text-sm font-medium text-foreground pt-4">
                  Role
                  <div className="relative sm:w-[384px] my-3">
                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem className={`space-y-1`}>
                          <FormControl>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <MultiSelector
                                    className={`bg-[hsl(var(--input))] border-[hsl(var(--border))] h-11 font-normal text-sm text-foreground
                                      ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    value={field.value}
                                    onChange={field.onChange}
                                    items={MEMBER_ROLE}
                                    disabled={!isAdmin}
                                  />
                                </div>
                              </TooltipTrigger>
                              {!isAdmin && (
                                <TooltipContent>
                                  Only administrators can set member roles
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </FormControl>
                          <FormMessage className="text-red-700 text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button type="submit" variant="secondary" disabled={!isAdmin}>
                          Send Invitation(s)
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!isAdmin && (
                      <TooltipContent>Only administrators can send invitations</TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 mt-8 sm:grid-cols-1 gap-4 justify-center ">
              <div className="col-span-1 sm:col-span-1 ">
                <div className="text-xl font-semibold text-foreground">Members</div>
              </div>
              <div className="col-span-1 sm:col-span-1 bg-card border border-gray rounded-lg">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="sm:h-[64px]">
                      <tr>
                        <th className="p-2 pl-4 py-4 text-left border-b border-gray font-medium text text-muted-foreground text-sm font-light sm:w-[370px]">
                          User
                        </th>
                        <th className="p-2 py-4 text-left border-b border-gray font-medium text text-muted-foreground text-sm font-light sm:w-[100px]">
                          Joined
                        </th>
                        <th className="p-2 py-4 text-left border-b border-gray font-medium text text-muted-foreground text-sm font-light">
                          Role
                        </th>
                        <th className="p-2 py-4 text-center border-b border-gray sm:w-[80px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {membersListData.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center p-4 text-muted-foreground">
                            {isAdmin
                              ? 'No team members yet. Invite some by entering their email above!'
                              : 'No team members to display. Contact an administrator to add team members.'}
                          </td>
                        </tr>
                      )}
                      {membersListData.map((user) => (
                        <tr
                          key={user.publicUserData.identifier}
                          className="border-t border-gray sm:h-[64px]"
                        >
                          <td className="p-2 pl-4 sm:flex items-center gap-2">
                            <img
                              src={user.publicUserData.imageUrl}
                              alt={user.publicUserData.firstName ?? ''}
                              className="w-10 h-10 rounded-full"
                            />
                            <div>
                              <div className="font-medium text-sm text-foreground">
                                {user.publicUserData.firstName} {user.publicUserData.lastName}
                              </div>
                              <div className="text-sm font-normal text-muted-foreground">
                                {user.publicUserData.identifier}
                              </div>
                            </div>
                          </td>
                          <td className="p-2 text-sm font-medium text-foreground">
                            {format(new Date(user.createdAt), 'M/d/yyyy')}
                          </td>
                          <td className="p-2">
                            <FormField
                              control={form.control}
                              name="memberRole"
                              render={({ field }) => (
                                <FormItem className={`space-y-1`}>
                                  <FormControl>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div>
                                          <MultiSelector
                                            className={`bg-[hsl(var(--input))] border-[hsl(var(--border))] font-normal text-sm text-foreground 
                                              ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            value={user.role.charAt(4) + user.role.slice(5)}
                                            onChange={(newValue) => {
                                              (field.onChange,
                                                void handleMembersRole(
                                                  user.publicUserData.userId ?? '',
                                                  newValue
                                                ));
                                            }}
                                            disabled={!isAdmin}
                                            items={MEMBER_ROLE}
                                          />
                                        </div>
                                      </TooltipTrigger>
                                      {!isAdmin && (
                                        <TooltipContent>
                                          Only administrators can change member roles
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </FormControl>
                                  <FormMessage className="text-red-700 text-xs" />
                                </FormItem>
                              )}
                            />
                          </td>
                          <td className="p-2 text-center">
                            <DropdownMenu>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      disabled={!isAdmin}
                                      className={!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}
                                    >
                                      <EllipsisVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                </TooltipTrigger>
                                {!isAdmin && (
                                  <TooltipContent>
                                    Only administrators can manage team members
                                  </TooltipContent>
                                )}
                              </Tooltip>
                              {isAdmin && (
                                <DropdownMenuContent className="rounded-lg" align="end">
                                  <DropdownMenuItem
                                    className="rounded-lg text-orangeLight"
                                    onClick={() => {
                                      void handleRemoveMember(user.publicUserData.userId ?? '');
                                    }}
                                  >
                                    Remove member
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              )}
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 mt-8 sm:grid-cols-1 gap-4 justify-center ">
              <div className="col-span-1 sm:col-span-1 ">
                <div className="text-xl font-semibold text-foreground">Invitations</div>
              </div>
              <div className="col-span-1 sm:col-span-3 bg-card border border-gray rounded-lg">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="sm:h-[64px]">
                      <tr>
                        <th className="p-2 pl-4 py-4 text-left border-b border-gray font-medium text text-muted-foreground text-sm font-light sm:w-[370px]">
                          User
                        </th>
                        <th className="p-2 py-4 text-left border-b border-gray font-medium text text-muted-foreground text-sm font-light sm:w-[100px]">
                          Invited
                        </th>
                        <th className="p-2 py-4 text-left border-b border-gray font-medium text text-muted-foreground text-sm font-light">
                          Role
                        </th>
                        <th className="p-2 py-4 text-center border-b border-gray sm:w-[80px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitationListData.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center p-4 text-gray-500 sm:h-[64px]">
                            {isAdmin
                              ? 'No invitations to display'
                              : 'No invitations to display. Contact an admin to invite team members.'}
                          </td>
                        </tr>
                      ) : (
                        invitationListData.map((user) => (
                          <tr key={user.emailAddress} className="border-t border-gray sm:h-[64px]">
                            <td className="p-2 pl-4 gap-2 text-sm font-normal text-muted-foreground">
                              {user.emailAddress}
                            </td>
                            <td className="p-2 text-sm font-medium text-foreground">
                              {' '}
                              {format(new Date(user.createdAt), 'M/d/yyyy')}
                            </td>
                            <td className="p-2">
                              {user.role.charAt(4).toUpperCase() + user.role.slice(5)}
                            </td>
                            <td className="p-2 text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <EllipsisVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="rounded-lg" align="end">
                                  <DropdownMenuItem
                                    className="rounded-lg text-orangeLight"
                                    onClick={() => {
                                      void handleRevokeInvitation(user);
                                    }}
                                  >
                                    Revoke Invitation
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </TooltipProvider>
  );
}
